/**
 * Cache em memória do inbox dinâmico (lista de conversas, config e últimas
 * conversas abertas). Ao voltar ao inbox a tela aparece na hora com o último
 * estado conhecido e é revalidada no servidor em seguida.
 *
 * Fica só em memória (nunca em localStorage/sessionStorage): são dados de
 * paciente e não podem sobreviver ao fechamento da aba nem ao logout.
 *
 * Com o inbox desmontado, o socket do MainLayout repassa os eventos em tempo
 * real para cá, mantendo a lista aquecida em segundo plano.
 */

export interface InboxListSnapshot<T> {
  conversations: T[];
  hasMore: boolean;
}

type ListRefresher = () => Promise<InboxListSnapshot<unknown>>;

interface ListEntry {
  channelId: string;
  conversations: unknown[];
  hasMore: boolean;
  savedAt: number;
  lastUsedAt: number;
  refresh?: ListRefresher;
}

interface ThreadEntry {
  messages: Array<{ id?: string; sent_at?: string; created_at?: string }>;
  savedAt: number;
}

const ENTRY_TTL_MS = 30 * 60 * 1000;
const MAX_LISTS = 12;
const MAX_THREADS = 15;
const MAX_CONFIGS = 10;
const BACKGROUND_DEBOUNCE_MS = 1500;
const BACKGROUND_MIN_INTERVAL_MS = 5000;

let scope = '';
const configs = new Map<string, { config: unknown; savedAt: number }>();
const lists = new Map<string, ListEntry>();
const threads = new Map<string, ThreadEntry>();
const leadStages = new Map<string, Record<string, number>>();
const mountedChannels = new Map<string, number>();
const backgroundTimers = new Map<string, number>();
const backgroundLastRun = new Map<string, number>();
const backgroundInflight = new Set<string>();

function putLru<V>(map: Map<string, V>, key: string, value: V, max: number): void {
  map.delete(key);
  map.set(key, value);
  while (map.size > max) {
    const oldest = map.keys().next().value;
    if (oldest === undefined) break;
    map.delete(oldest);
  }
}

function isExpired(savedAt: number): boolean {
  return Date.now() - savedAt > ENTRY_TTL_MS;
}

function messageTime(m: { sent_at?: string; created_at?: string }): number {
  return new Date(m.sent_at || m.created_at || 0).getTime();
}

function normalizeAttachments<T extends { attachments?: unknown }>(message: T): T {
  const raw = message.attachments;
  if (Array.isArray(raw)) return message;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return { ...message, attachments: Array.isArray(parsed) ? parsed : [] };
    } catch {
      return { ...message, attachments: [] };
    }
  }
  return { ...message, attachments: [] };
}

function clear(): void {
  scope = '';
  configs.clear();
  lists.clear();
  threads.clear();
  leadStages.clear();
  backgroundTimers.forEach((timer) => window.clearTimeout(timer));
  backgroundTimers.clear();
  backgroundLastRun.clear();
  backgroundInflight.clear();
}

/** Usuário/unidade mudou: nada do escopo anterior pode ser reaproveitado. */
function setScope(userId: string, unitId: string): void {
  const next = `${userId}|${unitId}`;
  if (next === scope) return;
  clear();
  scope = next;
}

function getConfig<T>(path: string): T | null {
  const entry = configs.get(path);
  if (!entry || isExpired(entry.savedAt)) {
    configs.delete(path);
    return null;
  }
  return entry.config as T;
}

function saveConfig(path: string, config: unknown): void {
  putLru(configs, path, { config, savedAt: Date.now() }, MAX_CONFIGS);
}

function listKey(channelId: string, query: string): string {
  return `${channelId}?${query}`;
}

function getList<T>(key: string): InboxListSnapshot<T> | null {
  const entry = lists.get(key);
  if (!entry || isExpired(entry.savedAt)) {
    lists.delete(key);
    return null;
  }
  entry.lastUsedAt = Date.now();
  return { conversations: entry.conversations as T[], hasMore: entry.hasMore };
}

function saveList<T>(
  key: string,
  channelId: string,
  snapshot: InboxListSnapshot<T>,
  refresh?: () => Promise<InboxListSnapshot<T>>,
): void {
  const previous = lists.get(key);
  const now = Date.now();
  putLru(
    lists,
    key,
    {
      channelId,
      conversations: snapshot.conversations,
      hasMore: snapshot.hasMore,
      savedAt: now,
      lastUsedAt: now,
      refresh: (refresh as ListRefresher | undefined) ?? previous?.refresh,
    },
    MAX_LISTS,
  );
}

function getThread<T>(conversationId: string): T[] | null {
  const entry = threads.get(conversationId);
  if (!entry || isExpired(entry.savedAt)) {
    threads.delete(conversationId);
    return null;
  }
  putLru(threads, conversationId, entry, MAX_THREADS);
  return entry.messages as T[];
}

function saveThread(conversationId: string, messages: ThreadEntry['messages']): void {
  if (!conversationId || messages.length === 0) return;
  putLru(threads, conversationId, { messages, savedAt: Date.now() }, MAX_THREADS);
}

function getLeadStages(channelId: string): Record<string, number> | null {
  return leadStages.get(channelId) ?? null;
}

function saveLeadStages(channelId: string, stages: Record<string, number>): void {
  leadStages.set(channelId, stages);
}

/** Com o inbox montado o socket dele é a fonte da verdade; o refresh em segundo plano pausa. */
function markChannelMounted(channelId: string): () => void {
  mountedChannels.set(channelId, (mountedChannels.get(channelId) || 0) + 1);
  const pending = backgroundTimers.get(channelId);
  if (pending) {
    window.clearTimeout(pending);
    backgroundTimers.delete(channelId);
  }
  return () => {
    const count = (mountedChannels.get(channelId) || 1) - 1;
    if (count <= 0) mountedChannels.delete(channelId);
    else mountedChannels.set(channelId, count);
  };
}

function mostRecentListKey(channelId: string): string | null {
  let bestKey: string | null = null;
  let bestUsedAt = -1;
  lists.forEach((entry, key) => {
    if (entry.channelId === channelId && entry.refresh && entry.lastUsedAt > bestUsedAt) {
      bestKey = key;
      bestUsedAt = entry.lastUsedAt;
    }
  });
  return bestKey;
}

async function runBackgroundRefresh(channelId: string): Promise<void> {
  if (mountedChannels.has(channelId) || backgroundInflight.has(channelId)) return;
  // Aba oculta: o inbox revalida ao montar, não vale gastar requisição agora.
  if (document.visibilityState === 'hidden') return;
  const key = mostRecentListKey(channelId);
  const entry = key ? lists.get(key) : undefined;
  if (!key || !entry?.refresh) return;
  if (isExpired(entry.savedAt)) {
    lists.delete(key);
    return;
  }

  const scopeAtStart = scope;
  backgroundInflight.add(channelId);
  backgroundLastRun.set(channelId, Date.now());
  try {
    const snapshot = await entry.refresh();
    if (scope !== scopeAtStart || mountedChannels.has(channelId)) return;
    const current = lists.get(key);
    if (!current) return;
    current.conversations = snapshot.conversations;
    current.hasMore = snapshot.hasMore;
    current.savedAt = Date.now();
  } catch {
    /* sem rede: o inbox revalida ao montar */
  } finally {
    backgroundInflight.delete(channelId);
  }
}

function scheduleBackgroundRefresh(channelId: string): void {
  if (mountedChannels.has(channelId) || backgroundTimers.has(channelId)) return;
  if (!mostRecentListKey(channelId)) return;
  const sinceLast = Date.now() - (backgroundLastRun.get(channelId) || 0);
  const wait = Math.max(BACKGROUND_DEBOUNCE_MS, BACKGROUND_MIN_INTERVAL_MS - sinceLast);
  const timer = window.setTimeout(() => {
    backgroundTimers.delete(channelId);
    void runBackgroundRefresh(channelId);
  }, wait);
  backgroundTimers.set(channelId, timer);
}

/** Recebe `new_message` / `conversation_updated` do socket global do layout. */
function handleRealtimeEvent(payload: any): void {
  if (!payload || !scope) return;
  const envelope = payload.update ? payload : null;
  const message = envelope ? null : payload.message || payload;
  const channelId =
    message?.channel_id
    || message?.channel?.id
    || payload.channel_id
    || envelope?.update?.channel_id
    || envelope?.update?.channel?.id
    || null;
  // Fail-closed: sem canal não dá para saber de qual inbox é o evento.
  if (!channelId) return;

  const conversationId = message?.conversation_id || payload.conversation_id;
  if (message?.id && conversationId) {
    const thread = threads.get(conversationId);
    if (thread && !thread.messages.some((m) => m.id === message.id)) {
      const incoming = normalizeAttachments({ ...message, conversation_id: conversationId });
      thread.messages = [...thread.messages, incoming].sort((a, b) => messageTime(a) - messageTime(b));
      thread.savedAt = Date.now();
    }
  }

  scheduleBackgroundRefresh(String(channelId));
}

export const inboxConversationCache = {
  clear,
  setScope,
  getConfig,
  saveConfig,
  listKey,
  getList,
  saveList,
  getThread,
  saveThread,
  getLeadStages,
  saveLeadStages,
  markChannelMounted,
  handleRealtimeEvent,
};

export default inboxConversationCache;
